/**
 * Adam AI Concierge Service - Frontend API Layer
 * Handles all API calls for the travel agent AI concierge
 */

import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance with auth
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});


// Auth interceptor
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// ==========================================
// AI Concierge Message Processing
// ==========================================

/**
 * Submit a message and get response (non-streaming)
 */
export const submitMessage = async (userMessage, chatId = null) => {
    try {
        const response = await api.post(
            '/adam/message',
            { userMessage, chatId },
            { timeout: 60000 }
        );
        return response.data;
    } catch (error) {
        console.error('Error in Adam submitMessage:', error);
        throw error;
    }
};

/**
 * Stream a response using Server-Sent Events
 */
export const streamMessage = async (userMessage, chatId, onMessage, onError, onComplete) => {
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE}/adam/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ userMessage, chatId })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6));
                        switch (data.type) {
                            case 'start':
                                onMessage && onMessage(data);
                                break;
                            case 'response':
                                onMessage && onMessage(data);
                                break;
                            case 'error':
                                onError && onError(data);
                                break;
                            case 'complete':
                                onComplete && onComplete(data);
                                return;
                            default:
                                onMessage && onMessage(data);
                        }
                    } catch (parseError) {
                        console.error('Error parsing Adam stream data:', parseError);
                    }
                }
            }
        }
    } catch (error) {
        console.error('Error in Adam streamMessage:', error);
        onError && onError({ message: error.message });
        throw error;
    }
};

// ==========================================
// Chat History Management
// ==========================================

/**
 * Create a new chat session
 */
export const createChat = async (initialMessage = null) => {
    try {
        const response = await api.post('/adam/chat', { initialMessage });
        return response.data;
    } catch (error) {
        console.error('Error creating chat:', error);
        throw error;
    }
};

/**
 * Get all chats for the travel agent
 */
export const getAgentChats = async (agentId, limit = 50, skip = 0) => {
    try {
        const response = await api.get(`/adam/chat/agent/${agentId}`, {
            params: { limit, skip }
        });
        return response.data;
    } catch (error) {
        console.error('Error getting agent chats:', error);
        throw error;
    }
};

/**
 * Get a single chat by ID
 */
export const getChatById = async (chatId, travelAgentId) => {
    try {
        const response = await api.get(`/adam/chat/${chatId}`, {
            params: { travelAgentId }
        });
        return response.data;
    } catch (error) {
        console.error('Error getting chat:', error);
        throw error;
    }
};

/**
 * Get or create active chat
 */
export const getActiveChat = async (agentId) => {
    try {
        const response = await api.get(`/adam/chat/active/${agentId}`);
        return response.data;
    } catch (error) {
        console.error('Error getting active chat:', error);
        throw error;
    }
};

/**
 * Add a single message to a chat
 */
export const addMessageToChat = async (chatId, travelAgentId, message) => {
    try {
        const response = await api.post(`/adam/chat/${chatId}/message`, {
            travelAgentId,
            message
        });
        return response.data;
    } catch (error) {
        console.error('Error adding message to chat:', error);
        throw error;
    }
};

/**
 * Add multiple messages to a chat
 */
export const addMessagesToChat = async (chatId, travelAgentId, messages) => {
    try {
        const response = await api.post(`/adam/chat/${chatId}/messages`, {
            travelAgentId,
            messages
        });
        return response.data;
    } catch (error) {
        console.error('Error adding messages to chat:', error);
        throw error;
    }
};

/**
 * Update chat title
 */
export const updateChatTitle = async (chatId, travelAgentId, title) => {
    try {
        const response = await api.patch(`/adam/chat/${chatId}/title`, {
            travelAgentId,
            title
        });
        return response.data;
    } catch (error) {
        console.error('Error updating chat title:', error);
        throw error;
    }
};

/**
 * Delete (archive) a chat
 */
export const deleteChat = async (chatId, travelAgentId) => {
    try {
        const response = await api.delete(`/adam/chat/${chatId}`, {
            params: { travelAgentId }
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting chat:', error);
        throw error;
    }
};

/**
 * Search chats
 */
export const searchChats = async (travelAgentId, searchTerm) => {
    try {
        const response = await api.get('/adam/chat/search', {
            params: { travelAgentId, q: searchTerm }
        });
        return response.data;
    } catch (error) {
        console.error('Error searching chats:', error);
        throw error;
    }
};

// ==========================================
// System
// ==========================================

/**
 * Health check
 */
export const healthCheck = async () => {
    try {
        const response = await api.get('/adam/health');
        return response.data;
    } catch (error) {
        console.error('Error checking Adam health:', error);
        throw error;
    }
};
